/**
 * Auth gate — delegates to a configurable AuthProvider.
 *
 * Only active when the bundle was built with NODE_ENV=production.
 *
 * TRUST MODEL:
 * - Tokens are stored in sessionStorage (tab-scoped, cleared on close).
 * - Tokens are verified LIVE on every API call by the backend.
 *   There is NO client-writable cache that can bypass the GitHub login allowlist.
 * - The backend holds the OAuth client_secret; the frontend never sees it.
 *
 * @see .grizzly/kb/sop-auth-provider-architecture.md
 */

import { getConfiguredProvider } from './registry.js'
import { renderLoginPage } from './login.js'
import { reportUnhandledError } from '../otel.js'

let _loginPageMounted = false

/**
 * Where the user was heading before they were asked to sign in.
 *
 * Google redirects to the registered redirect_uri exactly, and a URL fragment
 * does not survive that round trip — so someone who opens `/#/pair` while
 * signed out completes the login and lands on the default route, with no idea
 * why. That is not hypothetical: it is what happened on the first real device
 * pairing, and the person was never shown the field to type their code into.
 *
 * Saved before the login page mounts, restored once the callback succeeds.
 */
const POST_LOGIN_HASH_KEY = 'grizzly_post_login_hash'

function rememberDestination(): void {
  // Only a real destination. Storing an empty hash would overwrite a genuine
  // one saved by an earlier attempt.
  if (location.hash && location.hash !== '#/') {
    sessionStorage.setItem(POST_LOGIN_HASH_KEY, location.hash)
  }
}

function restoreDestination(url: URL): void {
  const saved = sessionStorage.getItem(POST_LOGIN_HASH_KEY)
  sessionStorage.removeItem(POST_LOGIN_HASH_KEY)
  if (saved) url.hash = saved
}

export async function requireAuth(): Promise<boolean> {
  // Built at compile time — esbuild replaces process.env.* with literal values
  if (process.env.NODE_ENV !== 'production') return true
  if (process.env.GRIZZLY_NO_AUTH === '1') return true

  const provider = getConfiguredProvider()
  const url = new URL(location.href)

  /**
   * A device pairing coming back from Google, not an ordinary sign-in.
   *
   * `state` is the only thing that survives the redirect, so it carries which
   * device is being authorised. Handled BEFORE the normal callback path
   * because the authorization code must go to the device-approval endpoint —
   * where it is exchanged with offline access so the DEVICE gets a renewable
   * credential. Feeding it to the ordinary handler would consume the code and
   * leave the waiting machine polling forever.
   */
  const state = url.searchParams.get('state') ?? ''
  const code = url.searchParams.get('code')
  if (code && state.startsWith('pair:')) {
    url.search = ''
    history.replaceState(null, '', `${url.toString()}#/pair`)
    const { completePairing } = await import('../galaxy/views/pair.js')
    await completePairing(state.slice('pair:'.length), code)
    // Deliberately false: the page has rendered its own result and there is
    // nothing else for the app to mount over it.
    return false
  }

  if (url.searchParams.has('code')) {
    const ok = await provider.handleCallback()
    if (ok) {
      url.search = ''
      restoreDestination(url)
      history.replaceState(null, '', url.toString())
      return true
    }
    mountLoginPage('Authentication failed. Please try again.')
    return false
  }

  if (provider.isAuthenticated()) {
    return true
  }

  rememberDestination()
  mountLoginPage()
  return false
}

export function initiateLogin(): void {
  try {
    getConfiguredProvider().initiateLogin()
  } catch (err) {
    reportUnhandledError(err instanceof Error ? err : new Error(String(err)), 'auth.initiate')
    mountLoginPage(err instanceof Error ? err.message : 'Login failed')
  }
}

export function signOut(): void {
  try {
    getConfiguredProvider().logout()
  } catch {
    // ignore
  }
  location.reload()
}

/**
 * The API rejected our credential — end the session and ask for a new one.
 *
 * WHY THIS EXISTS: a Google ID token lasts about an hour. Before this, nothing
 * reacted to it expiring. `requireAuth()` runs once at boot and only asked
 * whether a token string was present, so a stale token sailed through the gate;
 * every panel then failed its fetch with "Invalid or expired token" and the app
 * sat in a half-loaded state forever. Refreshing repeated it, because the dead
 * token was still in sessionStorage. Clearing site data by hand was the only
 * way back to the login page.
 *
 * The client-side expiry check in the provider catches the common case, but it
 * cannot be the only one: clock skew, a revoked session, or a key rotation all
 * produce a 401 on a token that still looks valid locally. The server's verdict
 * is the authority, so this is the path that actually guarantees recovery.
 *
 * Idempotent and one-shot — a dashboard fires several requests at once and they
 * will all 401 together; the login page must mount once, not once per panel.
 */
let _sessionEndedHandled = false
export function handleUnauthorized(): void {
  // Dev builds bypass the gate entirely, so there is no login page to show and
  // a 401 means something else is wrong. Leave it to the caller's error path.
  if (process.env.NODE_ENV !== 'production') return
  if (process.env.GRIZZLY_NO_AUTH === '1') return
  if (_sessionEndedHandled || _loginPageMounted) return
  _sessionEndedHandled = true

  try {
    getConfiguredProvider().logout()
  } catch {
    // A provider that cannot clear its own storage must not stop us from
    // showing the login page — that is the state the user is stuck in.
  }
  // Keep where they were, so signing back in returns them to it.
  rememberDestination()
  mountLoginPage('Your session expired. Please sign in again.')
}

export function getAuthToken(): string | null {
  try {
    return getConfiguredProvider().getToken()
  } catch {
    return null
  }
}

function mountLoginPage(errorMsg?: string): void {
  _loginPageMounted = true
  // Marks the whole document as the auth gate so the shell chrome — topbar
  // project picker, sidebar navigation — is hidden behind it. Both are
  // meaningless without a session, and they were visible around the card's
  // edges because this view mounts INSIDE the shell rather than replacing it.
  document.body.classList.add('is-login')
  const app = document.getElementById('app')
  if (app) {
    app.innerHTML = renderLoginPage(errorMsg)
  }
  const btn = document.getElementById('login-btn')
  if (btn) {
    btn.addEventListener('click', initiateLogin)
  }
}
